import { z } from 'zod';

/**
 * How a pet LOOKS: its energy colour and its chassis finish.
 *
 * ## The two axes never touch each other
 *
 * Energy owns everything that emits light - the neon in the model, the core,
 * the projectiles, muzzle flashes, particles, and later the butterfly's beam,
 * the drone's explosions, the missile trails and the technological circles.
 * Finish owns everything that reflects it - shell, chassis, plates, physical
 * trim.
 *
 * Keeping them disjoint is the whole point, and it is asserted by tests:
 * repainting a pet gold must not turn its bolts gold, and unlocking Void
 * Purple must not darken its armour. That is only possible because the models
 * assign materials SEMANTICALLY (`chassis`, `trim`, `emissive`, `energy`,
 * `projectile`, `particle`) rather than by hard-coded colour.
 *
 * ## What is owned where
 *
 * An UNLOCK is permanent and belongs to the ACCOUNT: consuming an Energy
 * Sphere or a Tech Paint Bucket adds one row to `account_pet_unlocks`, once,
 * and consuming a second copy of a colour already unlocked is refused without
 * spending the item.
 *
 * A SELECTION belongs to the pet INSTANCE, and travels with it: it survives
 * evolution, and when trading exists it will follow the item to its new owner
 * rather than staying behind with the account that painted it.
 */

/* -------------------------------------------------------------------------- */
/*  Energy colours                                                            */
/* -------------------------------------------------------------------------- */

/** Extra particle behaviour some colours carry, beyond the tint. */
export const PET_PARTICLE_PROFILES = [
  'none',
  /** Slow white motes, like stardust. */
  'stardust',
  /** Small dark flecks that fall inward. */
  'voidmotes',
  /** Drifting spore-like puffs. */
  'spores',
  /** Red and gold sparks, alternating. */
  'emberglint',
  /** Holographic, cycling through the spectrum. */
  'prismatic',
] as const;
export type PetParticleProfile = (typeof PET_PARTICLE_PROFILES)[number];

export interface PetEnergyColor {
  readonly id: string;
  readonly name: string;
  /** The base tint, as `0xRRGGBB`. */
  readonly color: number;
  /**
   * A second colour, for the two palettes that alternate.
   *
   * Null for every fixed colour. Anomaly alternates red and gold; Prismatic
   * cycles the spectrum and ignores both, driven by its own animation.
   */
  readonly secondaryColor: number | null;
  /** True for Prismatic, whose hue is a function of time rather than a value. */
  readonly animated: boolean;
  readonly particles: PetParticleProfile;
  /** Rarer colours are rarer drops. Weight is relative within the category. */
  readonly dropWeight: number;
}

/**
 * The starting palette.
 *
 * Every pet is born with exactly one of these unlocked - its own default -
 * and every other colour starts locked. Weights make the plain colours common
 * and the two special palettes genuinely rare.
 */
export const PET_ENERGY_COLORS: readonly PetEnergyColor[] = Object.freeze([
  {
    id: 'energy_celestial_white',
    name: 'Branco Celestial',
    color: 0xf2f6ff,
    secondaryColor: null,
    animated: false,
    particles: 'stardust',
    dropWeight: 100,
  },
  {
    id: 'energy_plasma_red',
    name: 'Vermelho Plasma',
    color: 0xff1a14,
    secondaryColor: null,
    animated: false,
    particles: 'none',
    dropWeight: 100,
  },
  {
    id: 'energy_ion_blue',
    name: 'Azul Íon',
    color: 0x2a8bff,
    secondaryColor: null,
    animated: false,
    particles: 'none',
    dropWeight: 100,
  },
  {
    id: 'energy_void_purple',
    name: 'Roxo do Vazio',
    color: 0x9b2dff,
    secondaryColor: null,
    animated: false,
    particles: 'voidmotes',
    dropWeight: 80,
  },
  {
    id: 'energy_toxic_green',
    name: 'Verde Tóxico',
    color: 0x4dff5a,
    secondaryColor: null,
    animated: false,
    particles: 'spores',
    dropWeight: 80,
  },
  {
    id: 'energy_solar_yellow',
    name: 'Amarelo Solar',
    color: 0xffd21a,
    secondaryColor: null,
    animated: false,
    particles: 'none',
    dropWeight: 90,
  },
  {
    id: 'energy_glacial_cyan',
    name: 'Ciano Glacial',
    color: 0x2ff2e6,
    secondaryColor: null,
    animated: false,
    particles: 'none',
    dropWeight: 90,
  },
  {
    id: 'energy_nova_pink',
    name: 'Rosa Nova',
    color: 0xff44b0,
    secondaryColor: null,
    animated: false,
    particles: 'none',
    dropWeight: 80,
  },
  {
    id: 'energy_anomaly',
    name: 'Anomaly',
    color: 0xff2010,
    secondaryColor: 0xffc23a,
    animated: true,
    particles: 'emberglint',
    dropWeight: 12,
  },
  {
    id: 'energy_prismatic',
    name: 'Prismatic',
    color: 0xffffff,
    secondaryColor: null,
    animated: true,
    particles: 'prismatic',
    dropWeight: 6,
  },
]);

const ENERGY_BY_ID = new Map(PET_ENERGY_COLORS.map((entry) => [entry.id, entry]));

export function findEnergyColor(id: string): PetEnergyColor | null {
  return ENERGY_BY_ID.get(id) ?? null;
}

export function requireEnergyColor(id: string): PetEnergyColor {
  const found = ENERGY_BY_ID.get(id);
  if (!found) {
    throw new RangeError(`unknown pet energy colour: ${id}`);
  }
  return found;
}

/* -------------------------------------------------------------------------- */
/*  Chassis finishes                                                          */
/* -------------------------------------------------------------------------- */

/**
 * A finish is a MATERIAL, not a colour swatch.
 *
 * Gold that is merely yellow reads as plastic; chrome that does not reflect
 * reads as grey paint. Each entry therefore carries the physical parameters
 * the renderer feeds straight into `MeshPhysicalMaterial`, and the two special
 * ones declare the extra terms they need.
 */
export interface PetChassisFinish {
  readonly id: string;
  readonly name: string;
  readonly color: number;
  readonly metalness: number;
  readonly roughness: number;
  /** Clear-coat over the base, for the lacquered finishes. */
  readonly clearcoat: number;
  /** Thin-film iridescence, for the holographic finish. */
  readonly iridescence: number;
  /**
   * Refractive index of the thin film, when there is one.
   *
   * This is what decides how SATURATED the interference colours are, and
   * leaving it at the 1.3 default is why an early holographic finish read as
   * grey with a faint sheen: at an index that close to air the film barely
   * splits the spectrum. Values near 2 give the vivid cyan/magenta/gold banding
   * a real dichroic coating has. Ignored when `iridescence` is 0.
   */
  readonly iridescenceIOR?: number;
  /**
   * Metallic flake, 0 to 1: the glitter suspended in an automotive paint.
   *
   * Rendered as a fine random perturbation of the surface normal under the
   * clear coat, so individual flakes catch the light one at a time as the model
   * turns. It is a property of the PAINT, never of the energy.
   */
  readonly flake?: number;
  readonly dropWeight: number;
}

export const PET_CHASSIS_FINISHES: readonly PetChassisFinish[] = Object.freeze([
  {
    id: 'finish_pearl_white',
    name: 'Branco Pérola',
    color: 0xe8ecf4,
    metalness: 0.55,
    roughness: 0.22,
    clearcoat: 0.9,
    iridescence: 0,
    dropWeight: 100,
  },
  {
    id: 'finish_obsidian_black',
    name: 'Preto Obsidiana',
    color: 0x0a0b0e,
    metalness: 0.7,
    roughness: 0.2,
    clearcoat: 0.8,
    iridescence: 0,
    dropWeight: 100,
  },
  {
    id: 'finish_graphite',
    name: 'Grafite',
    color: 0x3c4048,
    metalness: 0.8,
    roughness: 0.42,
    clearcoat: 0.2,
    iridescence: 0,
    dropWeight: 100,
  },
  {
    id: 'finish_metallic_red',
    name: 'Vermelho Metálico',
    color: 0x8e1219,
    metalness: 0.85,
    roughness: 0.25,
    clearcoat: 0.7,
    iridescence: 0,
    dropWeight: 90,
  },
  {
    id: 'finish_metallic_blue',
    name: 'Azul Metálico',
    color: 0x14386e,
    metalness: 0.85,
    roughness: 0.25,
    clearcoat: 0.7,
    iridescence: 0,
    dropWeight: 90,
  },
  {
    id: 'finish_metallic_purple',
    name: 'Roxo Metálico',
    color: 0x4a1670,
    metalness: 0.85,
    roughness: 0.25,
    clearcoat: 0.7,
    iridescence: 0,
    dropWeight: 90,
  },
  {
    id: 'finish_metallic_green',
    name: 'Verde Metálico',
    color: 0x14602c,
    metalness: 0.85,
    roughness: 0.25,
    clearcoat: 0.7,
    iridescence: 0,
    dropWeight: 90,
  },
  {
    id: 'finish_copper',
    name: 'Cobre',
    color: 0xb87333,
    metalness: 1,
    roughness: 0.28,
    clearcoat: 0,
    iridescence: 0,
    dropWeight: 60,
  },
  {
    id: 'finish_bronze',
    name: 'Bronze',
    color: 0xa57332,
    metalness: 1,
    roughness: 0.34,
    clearcoat: 0,
    iridescence: 0,
    dropWeight: 60,
  },
  {
    id: 'finish_polished_silver',
    name: 'Prata Polida',
    color: 0xd8dde5,
    metalness: 1,
    roughness: 0.08,
    clearcoat: 0,
    iridescence: 0,
    dropWeight: 40,
  },
  {
    id: 'finish_polished_gold',
    name: 'Ouro Polido',
    // A real gold albedo at full metalness: yellow paint would read as plastic.
    color: 0xffc23a,
    metalness: 1,
    roughness: 0.12,
    clearcoat: 0,
    iridescence: 0,
    dropWeight: 18,
  },
  {
    id: 'finish_chrome',
    name: 'Cromo',
    color: 0xf0f4fa,
    metalness: 1,
    roughness: 0.02,
    clearcoat: 0,
    iridescence: 0,
    dropWeight: 22,
  },
  {
    id: 'finish_black_chrome',
    name: 'Cromo Negro',
    // Dark but NOT black: at roughness 0.05 the environment supplies the
    // highlights, and a pure black albedo would swallow them.
    color: 0x14171c,
    metalness: 1,
    roughness: 0.05,
    // A clear coat over the flake, which is what a real flake paint is: the
    // glitter sits UNDER a smooth lacquer, so the sparkle twinkles while the
    // body highlight stays a clean sheet.
    clearcoat: 1,
    iridescence: 0,
    flake: 0.85,
    dropWeight: 22,
  },
  {
    id: 'finish_rose_gold',
    name: 'Ouro Rosé',
    color: 0xe0a08a,
    metalness: 1,
    roughness: 0.14,
    clearcoat: 0,
    iridescence: 0,
    dropWeight: 16,
  },
  {
    id: 'finish_holographic',
    name: 'Holográfico Prismatic',
    /*
     * A DARK base, deliberately.
     *
     * This used to be a light grey, and that is exactly why the finish read as
     * grey: a bright diffuse base drowns the thin film, because interference
     * colours are added on top of whatever the surface already reflects. A
     * near-black substrate under a strong film is how dichroic glass and
     * oil-slick coatings actually work - the colour a viewer sees IS the film.
     */
    color: 0x0b0e16,
    metalness: 0.65,
    roughness: 0.06,
    clearcoat: 1,
    // Thin-film interference: the hue shifts with the viewing angle, which is
    // what "holographic" means physically rather than "rainbow texture".
    iridescence: 1,
    iridescenceIOR: 2.2,
    dropWeight: 5,
  },
]);

const FINISH_BY_ID = new Map(PET_CHASSIS_FINISHES.map((entry) => [entry.id, entry]));

export function findChassisFinish(id: string): PetChassisFinish | null {
  return FINISH_BY_ID.get(id) ?? null;
}

export function requireChassisFinish(id: string): PetChassisFinish {
  const found = FINISH_BY_ID.get(id);
  if (!found) {
    throw new RangeError(`unknown pet chassis finish: ${id}`);
  }
  return found;
}

/* -------------------------------------------------------------------------- */
/*  Wire                                                                      */
/* -------------------------------------------------------------------------- */

export const petUnlockKinds = ['energy', 'finish'] as const;
export type PetUnlockKind = (typeof petUnlockKinds)[number];
export const petUnlockKindSchema = z.enum(petUnlockKinds);

export const petEnergyColorWireSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  color: z.number().int().min(0),
  secondaryColor: z.number().int().min(0).nullable(),
  animated: z.boolean(),
  particles: z.enum(PET_PARTICLE_PROFILES),
  unlocked: z.boolean(),
  /** The consumable that unlocks it, so a locked row can name its key. */
  unlockItemId: z.string().min(1),
});
export type PetEnergyColorWire = z.infer<typeof petEnergyColorWireSchema>;

export const petChassisFinishWireSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  color: z.number().int().min(0),
  metalness: z.number().min(0).max(1),
  roughness: z.number().min(0).max(1),
  clearcoat: z.number().min(0).max(1),
  iridescence: z.number().min(0).max(1),
  // Optional because only the finishes that need a film or a flake carry them;
  // the renderer supplies the physical defaults for everything else.
  iridescenceIOR: z.number().min(1).max(3).optional(),
  flake: z.number().min(0).max(1).optional(),
  unlocked: z.boolean(),
  unlockItemId: z.string().min(1),
});
export type PetChassisFinishWire = z.infer<typeof petChassisFinishWireSchema>;

/* -------------------------------------------------------------------------- */
/*  The consumables that unlock them                                          */
/* -------------------------------------------------------------------------- */

/** `energy_ion_blue` is unlocked by `sphere_energy_ion_blue`. */
export function energyUnlockItemId(energyId: string): string {
  return `sphere_${energyId.replace(/^energy_/, 'energy_')}`;
}

/** `finish_chrome` is unlocked by `paint_finish_chrome`. */
export function finishUnlockItemId(finishId: string): string {
  return `paint_${finishId}`;
}
